import type { UseQueryResult } from '@tanstack/react-query';
import { useMemo, type PropsWithChildren } from 'react';
import {
  KindePropertiesContext,
  type QueryKindePropertiesQueryResult,
  type QueryUserKindeProperties,
} from './kinde-properties-context';

interface KindePropertiesProviderProps extends PropsWithChildren {
  kindePropertiesQuery: UseQueryResult<QueryUserKindeProperties, Error>;
}

const toKindePropertiesQueryResult = (
  query: UseQueryResult<QueryUserKindeProperties, Error>
): QueryKindePropertiesQueryResult => ({
  data: query.data,
  isLoading: query.isLoading,
  isPending: query.isPending,
  isSuccess: query.isSuccess,
  isError: query.isError,
  error: query.error,
  isFetching: query.isFetching,
  refetch: query.refetch,
});

export const KindePropertiesProvider = ({
  children,
  kindePropertiesQuery,
}: KindePropertiesProviderProps) => {
  // Not an effect-mirror: first commit used to publish idle `initialKindePropertiesQueryResult`
  // (`isPending: true, isFetching: false`) and consumers painted no-data UI for a frame.
  const kindePropertiesQueryResult = useMemo(
    () => toKindePropertiesQueryResult(kindePropertiesQuery),
    [kindePropertiesQuery]
  );

  return (
    <KindePropertiesContext.Provider value={kindePropertiesQueryResult}>
      {children}
    </KindePropertiesContext.Provider>
  );
};
